/**
 * Auth to Fund Event Mapper
 *
 * This module provides utilities to map Connect Auth callbacks to Fund postMessage events
 * for backward compatibility. When using @connect-xyz/auth-react instead of Fund iframe,
 * we need to translate Auth's callback-based API into Fund's postMessage-based API.
 *
 * Mapping:
 * - Auth onLoaded() -> FUND_APP_LOADED
 * - Auth onClose() -> FUND_CLOSE_BUTTON_CLICKED
 * - Auth onDeposit() with success status -> FUND_COMPLETED
 * - Auth onDeposit() with failed status -> FUND_FAILED
 * - Auth onError() -> FUND_ERROR (new event)
 * - Auth onEvent('deposit.submitted') -> FUND_DEPOSIT_SUBMITTED (new event)
 */

import { IncomingMessageType } from "../types";

/**
 * Auth callback types from @connect-xyz/auth-react
 */
export type AuthDepositPayload = {
  data: {
    depositId: string;
    status: {
      value: string;
      details: string;
      occurredAt: string;
    };
    assetId: string;
    networkId: string;
    amount?: string;
  };
};

export type AuthErrorPayload = {
  errorCode: string;
  reason: string;
};

export type AuthEvent = {
  type: string;
  data: {
    depositId: string;
  };
};

/**
 * Creates Auth callbacks that automatically emit Fund-compatible postMessages
 * to the parent window for backward compatibility.
 *
 * @param origin - The origin to send postMessages to (typically window.location.origin)
 * @returns Object with Auth callback functions
 */
export function createAuthToFundCallbacks(origin: string) {
  const sendPostMessage = (type: IncomingMessageType, payload?: unknown) => {
    window.postMessage(
      {
        type,
        payload,
      },
      origin
    );
  };

  return {
    /**
     * Maps Auth onLoaded to FUND_APP_LOADED
     */
    onLoaded: () => {
      sendPostMessage(IncomingMessageType.StyleConfig)
      sendPostMessage(IncomingMessageType.FundAppLoaded);
    },

    /**
     * Maps Auth onClose to FUND_CLOSE_BUTTON_CLICKED
     */
    onClose: () => {
      sendPostMessage(IncomingMessageType.FundCloseButtonClicked);
    },

    /**
     * Maps Auth onDeposit to FUND_COMPLETED or FUND_FAILED
     * based on the deposit status value.
     * Also sends FUND_CONNECT_DEPOSIT event for Connect-specific deposit handling.
     */
    onDeposit: (deposit: AuthDepositPayload) => {
      const isSuccess = isDepositSuccessful(deposit.data.status.value);

      const payload = {
        transactionId: deposit.data.depositId,
        fundId: deposit.data.depositId,
        assetId: deposit.data.assetId,
        networkId: deposit.data.networkId,
        amount: deposit.data.amount,
        status: deposit.data.status,
      };

      // Send Connect-specific deposit event (new event exclusive to Connect Auth)
      sendPostMessage(IncomingMessageType.FundConnectDeposit, payload);

      // Send legacy FUND_COMPLETED or FUND_FAILED for backward compatibility
      if (isSuccess) {
        sendPostMessage(IncomingMessageType.FundCompleted, payload);
      } else {
        sendPostMessage(IncomingMessageType.FundFailed, payload);
      }
    },

    /**
     * Maps Auth onError to FUND_ERROR
     */
    onError: (error: AuthErrorPayload) => {
      sendPostMessage(IncomingMessageType.StyleConfig);
      sendPostMessage(IncomingMessageType.FundError, {
        errorCode: error.errorCode,
        reason: error.reason,
      });
    },

    /**
     * Maps Auth onEvent to appropriate Fund events
     * Handles 'deposit.submitted' -> FUND_DEPOSIT_SUBMITTED
     * All other events are caught by default case -> FUND_CONNECT_EVENT (catch-all)
     */
    onEvent: (event: AuthEvent) => {
      if (event.type === 'deposit.submitted') {
        sendPostMessage(IncomingMessageType.FundDepositSubmitted, {
          depositId: event.data.depositId,
        });
      } else {
        // Default case: catch-all for any other Auth events
        sendPostMessage(IncomingMessageType.FundConnectEvent, {
          type: event.type,
          data: event.data,
        });
      }
    },
  };
}

/**
 * Determines if a deposit was successful based on the status value
 * @param statusValue - The status value from the deposit
 * @returns true if deposit was successful, false otherwise
 */
function isDepositSuccessful(statusValue: string): boolean {
  const successStatuses = [
    'completed',
    'success',
    'confirmed',
    'settled',
    'processed',
  ];

  const failureStatuses = [
    'failed',
    'rejected',
    'cancelled',
    'error',
  ];

  const normalizedStatus = statusValue.toLowerCase();

  if (successStatuses.includes(normalizedStatus)) {
    return true;
  }

  if (failureStatuses.includes(normalizedStatus)) {
    return false;
  }

  return false;
}
